import { ChangeEvent, memo, useMemo, FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@material-ui/core';
import {
  MeterModel,
  Option,
  SortParams,
  SortParamsArg,
  TableCells,
  ValidatedMeter,
  MeterIndication,
  IndicationStatus,
} from 'models';
import { meterReadingTableCells } from 'configs';
import { formatTableData } from 'utils';

import { PageTitle } from 'containers';
import {
  TableCell,
  TableRow,
  TextField,
  styled,
  InputProps,
} from '@material-ui/core';
import {
  PageLoader,
  Error,
  FiltersBar,
  MeterReadingNotification,
  TableSearchBar,
  ControlWrapper,
  Select,
  DatePicker,
  Table,
  ButtonShape,
  Loader,
  Centered,
} from 'components';

import clsx from 'clsx';
import { useStyles } from './style';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '12px 10px',
  fontSize: 14,
  fontFamily: 'NeoSansStd',
  background: '#fff',
  color: theme.custom.darkBlue,
}));

function getWarningText(status: IndicationStatus | undefined) {
  switch (status) {
    case 1:
      return '#portal.maintenance.meters.message.notcorrect.lessthenprevious';
    case 2:
      return '#portal.maintenance.meters.message.notcorrect.biggerthennext';
    case 3:
      return '#portal.maintenance.meters.message.notcorrect.exists';
    default:
      return '';
  }
}

const options1 = [
  { Label: '#portal.maintenance.meters.options.main', Value: 'main' },
  { Label: '#portal.maintenance.meters.options.space', Value: 'space' },
  { Label: '#portal.maintenance.meters.options.both', Value: 'both' },
];

const options2 = [
  { Label: '#portal.maintenance.meters.options.active', Value: 'active' },
  { Label: '#portal.maintenance.meters.options.passive', Value: 'passive' },
  { Label: '#portal.maintenance.meters.options.both', Value: 'both' },
];

interface Props {
  meters: MeterModel[];
  loading: boolean;
  error: boolean;
  refresh: boolean;
  validateStatus: boolean;
  creating: boolean;
  createError: boolean;
  notification: boolean;
  searchTerm: string;
  date: Date | null;
  filter1: Option;
  filter2: Option;
  sortParams: SortParams;
  validatedMeters: ValidatedMeter[];
  readyMeters: MeterIndication[];
  createMeters: () => Promise<void>;
  handleCloseNotification: () => void;
  handleChangeInputField: (
    e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    Id: string | null,
    IndicationId: string | null
  ) => void;
  handleChangeSelect: (option: Option, name?: string) => void;
  handleChangeDate: (date: Date | null) => void;
  handleChangeSearchTerm: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangeSortParams: (params: SortParamsArg) => void;
}

const MeterReadingDetails: React.FC<Props> = ({
  meters,
  loading,
  error,
  refresh,
  readyMeters,
  notification,
  createError,
  creating,
  searchTerm,
  date,
  filter2,
  filter1,
  validatedMeters,
  sortParams,
  handleChangeSelect,
  validateStatus,
  handleChangeDate,
  handleChangeSearchTerm,
  handleChangeSortParams,
  handleChangeInputField,
  handleCloseNotification,
  createMeters,
}) => {
  const theme = useTheme();
  const sm = theme.breakpoints.down(theme.size.sm);
  const xs = theme.breakpoints.down(theme.size.xs);
  const { t } = useTranslation();
  const classes = useStyles();

  const selectCls = useMemo(
    () => ({
      value: classes.value,
      label: classes.label,
      btn: {
        root: classes.btn,
      },

      /* eslint-disable-next-line */
    }),
    []
  );

  const inputProps: InputProps = useMemo(
    () => ({
      disableUnderline: true,
      classes: {
        input: classes.input,
      },
    }),
    [classes]
  );

  const renderRow = (
    meter: MeterModel,
    tableCells: TableCells<MeterModel>[]
  ) => {
    return (
      <TableRow key={meter.Id}>
        {tableCells.map((tableCell) => {
          if (tableCell.id === 'Indication') {
            const validatedMeter = validatedMeters.find(
              (validatedMeter) => validatedMeter.MeterId === meter.Id
            );

            return (
              <StyledTableCell key={tableCell.id}>
                <TextField
                  className={clsx(classes.textField, {
                    [classes.successField]:
                      validatedMeter && validatedMeter.Status === 0,
                    [classes.invalidField]:
                      validatedMeter && validatedMeter.Status !== 0,
                  })}
                  disabled={validateStatus || creating}
                  type="number"
                  defaultValue={validatedMeter?.Value || meter.Indication}
                  placeholder={t(
                    '#portal.maintenance.meters.message.entervalue'
                  )}
                  InputProps={inputProps}
                  onBlur={(e) =>
                    handleChangeInputField(e, meter.Id, meter.IndicationId)
                  }
                />
                {validatedMeter && validatedMeter?.Status !== 0 ? (
                  <p className={classes.warningText}>
                    {t(getWarningText(validatedMeter?.Status))}
                  </p>
                ) : null}
              </StyledTableCell>
            );
          }

          return (
            <StyledTableCell key={tableCell.id}>
              {formatTableData(meter, tableCell)}
            </StyledTableCell>
          );
        })}
      </TableRow>
    );
  };

  const renderFooter = () => {
    return (
      <div className={classes.footer}>
        <ButtonShape
          className={classes.saveBtn}
          onClick={createMeters}
          disabled={creating || validateStatus || !readyMeters.length}
        >
          {t('#portal.maintenance.meters.button.save')}
          {creating ? (
            <Centered className={classes.ml15}>
              <Loader size={18} cls={{ root: classes.loader }} />
            </Centered>
          ) : null}
        </ButtonShape>
        <MeterReadingNotification
          open={notification}
          error={createError}
          handleClose={handleCloseNotification}
        />
      </div>
    );
  };

  if (loading) {
    return <PageLoader />;
  }

  if (error) {
    return <Error />;
  }

  return (
    <div className={classes.root}>
      <PageTitle />
      <FiltersBar className={classes.filtersBar}>
        <ControlWrapper>
          <span className={classes.label}>
            {t('#portal.maintenance.meters.readingdate')}:
          </span>
          <DatePicker date={date} handleChange={handleChangeDate} />
        </ControlWrapper>
        <ControlWrapper>
          <span className={classes.label}>
            {t('#portal.maintenance.meters.options')}:
          </span>
          <Select
            name="filter1"
            value={filter1}
            options={options1}
            onSelect={handleChangeSelect}
            labelField="Label"
            valueField="Value"
            cls={selectCls}
            withTranslation
          />
        </ControlWrapper>
        <ControlWrapper>
          <Select
            name="filter2"
            value={filter2}
            options={options2}
            onSelect={handleChangeSelect}
            labelField="Label"
            valueField="Value"
            cls={selectCls}
            withTranslation
          />
        </ControlWrapper>
        <ControlWrapper>
          <TableSearchBar
            value={searchTerm}
            onChange={handleChangeSearchTerm}
          />
        </ControlWrapper>
      </FiltersBar>
      <Table
        list={meters}
        loading={refresh}
        order={sortParams.order}
        orderBy={sortParams.orderBy}
        tableCells={meterReadingTableCells}
        handleChangeSortParams={handleChangeSortParams}
        renderRow={renderRow}
      />
      {refresh ? null : renderFooter()}
    </div>
  );
};

export default memo(MeterReadingDetails);
